<template>
  <div class="feedback-content">
    <span class="feedback-content__title">What would you like to change?</span>
    <Button
      color="green"
      full
      :rounded-full="false"
      :invert="currentSelection !== Selections.CUSTOMIZE_DRAFT_ITINERARY"
      @click="handleSelection(Selections.CUSTOMIZE_DRAFT_ITINERARY)"
      ><div class="flex-col">
        <span>Customize draft itinerary</span>
        <span class="selected" v-if="currentSelection === Selections.CUSTOMIZE_DRAFT_ITINERARY"
          >You'll go back to customize your draft itinerary</span
        >
      </div>
    </Button>
    <Button
      color="green"
      full
      :rounded-full="false"
      :invert="currentSelection !== Selections.CHANGE_WORDING_OR_DESCRIPTIONS"
      @click="handleSelection(Selections.CHANGE_WORDING_OR_DESCRIPTIONS)"
      ><div class="flex-col">
        <span>Change wording or descriptions</span>
        <span class="selected" v-if="currentSelection === Selections.CHANGE_WORDING_OR_DESCRIPTIONS"
          >In the next step, we'll ask you to provide a few more details</span
        >
      </div></Button
    >
    <Button
      color="green"
      full
      :rounded-full="false"
      :invert="currentSelection !== Selections.CHANGE_DATES_GROUP_SIZE_MARKUP"
      @click="handleSelection(Selections.CHANGE_DATES_GROUP_SIZE_MARKUP)"
      ><div class="flex-col">
        <span>Change dates, group size, markup</span>
        <span class="selected" v-if="currentSelection === Selections.CHANGE_DATES_GROUP_SIZE_MARKUP"
          >You'll go back to setup booking details</span
        >
      </div></Button
    >
    <Button
      color="green"
      full
      :rounded-full="false"
      :invert="currentSelection !== Selections.CHANGE_SOMETHING_ELSE"
      @click="handleSelection(Selections.CHANGE_SOMETHING_ELSE)"
      ><div class="flex-col">
        <span>Something else</span>
        <span class="selected" v-if="currentSelection === Selections.CHANGE_SOMETHING_ELSE"
          >You'll go to messages to share more</span
        >
      </div></Button
    >
    <Button color="red" @click="handleChangeSomethingNext" :disabled="loading || !currentSelection">Continue</Button>
    <span class="feedback-content__cancel" @click="emits('go-to-stage', Stages.DEFAULT)">Back</span>
  </div>
</template>

<script setup lang="ts">
import { Stages, Selections, SelectionForStage } from './types'
import { ref } from 'vue'
import Button from '@/components/Theme2/Button.vue'
import { Product } from 'app/javascript/src/models'
import { useTypedItineraryStore } from 'app/javascript/src/stores/typed'
import { redirectTo } from 'app/javascript/src/utils/functions'
import api from 'app/javascript/src/utils/api'
import { useAuthUserState } from 'app/javascript/src/stores'
import { logErrorOnSentry } from 'app/javascript/src/utils/errorReportingUtils'
import { useShared } from 'app/javascript/src/helpers/vueHelpers'

const emits = defineEmits<{
  (e: 'go-to-stage', stage: Stages): void
}>()

const itineraryStore = useTypedItineraryStore<Product>()
const userAuth = useAuthUserState()
const { componentInfo } = useShared()

const currentSelection = ref<SelectionForStage<Stages.CHANGE_SOMETHING> | null>(null)
const loading = ref(false)

const handleSelection = (selection: SelectionForStage<Stages.CHANGE_SOMETHING>) => {
  if (currentSelection.value === selection) {
    currentSelection.value = null
    return
  }

  currentSelection.value = selection
}

const handleChangeSomethingNext = async () => {
  switch (currentSelection.value) {
    case Selections.CUSTOMIZE_DRAFT_ITINERARY:
      if (itineraryStore.data?.draftItineraryUrl) {
        redirectTo(itineraryStore.data?.draftItineraryUrl?.replace('/setup', ''))
      }
      break
    case Selections.CHANGE_WORDING_OR_DESCRIPTIONS:
      emits('go-to-stage', Stages.CHANGE_WORDING_OR_DESCRIPTIONS)
      break
    case Selections.CHANGE_DATES_GROUP_SIZE_MARKUP:
      if (itineraryStore.data?.draftItineraryUrl) {
        redirectTo(itineraryStore.data?.draftItineraryUrl)
      }
      break
    case Selections.CHANGE_SOMETHING_ELSE:
      loading.value = true
      try {
        await api.post(`/api/chatrooms/${itineraryStore.chatroomId}/messages/self_serve_message`, {
          selections: [currentSelection.value],
          sender_name: userAuth.name.value,
        })
        if (itineraryStore?.data?.itineraryRequestId) {
          redirectTo(`/travelers/messages?itineraryRequestId=${itineraryStore.data.itineraryRequestId}`)
        }
      } catch (error) {
        logErrorOnSentry(error, componentInfo)
        loading.value = false
      }
      break
    default:
      break
  }
}
</script>

<style lang="scss" scoped>
.selected {
  font-size: $text-sm;
  color: $grey-50;
  font-weight: $font-normal;
}
</style>
